<template>
	<el-dialog
		:modelValue="open"
		@update:modelValue="$emit('update:open', $event)"
		:title="currentAbsence ? 'Abwesenheit bearbeiten' : 'Abwesenheit erstellen'"
		:before-close="(done) => dialogBeforeClose(done, waiting)"
	>
		<p>
			Während einer Abwesenheit erhalten User keine Einladungen zu Abstimmungen und Entscheidungen, Nach Ablauf der Abwesenheit ändert sich der Status
			wieder automatisch zu „aktiv".
		</p>
		<TheForm
			:model="request"
			:rules="rules"
			:waiting="waiting"
			:waitingMsg="texts.formErrors.waiting"
			:showErr="showErr"
			:errMsg="errMsg"
			ref="formRef"
			:id="id"
		>
			<el-form-item label="" prop="dateRange" class="el-col">
				<div class="datepicker-range__labels">
					<label class="el-form-item__label">Von (Starttag ab 00:00 Uhr)</label>
					<label class="el-form-item__label">Bis (Endtag bis 23:59 Uhr)</label>
				</div>
				<el-date-picker
					v-model="request.dateRange"
					type="daterange"
					:format="'DD.MM.YYYY'"
					range-separator=""
					:clearable="true"
					start-placeholder="TT.MM.JJJJ"
					end-placeholder="TT.MM.JJJJ"
					:disabled-date="checkIfDateInPast"
				/>
			</el-form-item>
		</TheForm>

		<template #footer>
			<div class="button-panel">
				<TheButton type="danger" @buttonclick="setUserAbsence" :disabled="waiting" :loading="waiting">Abwesenheit speichern</TheButton>

				<TheButton type="default" @buttonclick="deleteUserAbsence" :disabled="waiting" v-if="currentAbsence" :loading="waiting"
					>Abwesenheit löschen</TheButton
				>
				<TheButton type="default" @buttonclick="handleClose" v-else>Abbrechen</TheButton>
			</div>
		</template>
	</el-dialog>
</template>

<script lang="ts">
import { createUserAbsence, deleteUserAbsence } from '@/requests/users';
import { dialogBeforeClose, resetForm, validateForm, getStartOfDayWithTime, getEndOfDayWithTime } from '@/utils/functions';
import { checkIfDateInPast } from '@/utils/search/searchUtils';
import type { FormInstance } from 'element-plus';
import { defineComponent, ref } from 'vue';
import { AbsenceRequest, UserAbsenceResponse } from '../../types/types';
import { texts } from '../../utils/texts';
import TheButton from '../TheButton.vue';
import TheForm from '../TheForm.vue';

export default defineComponent({
	name: 'DialogEditUserAbsence',
	components: {
		TheButton,
		TheForm,
	},
	props: {
		open: {
			type: Boolean,
			required: true,
		},
		userId: {
			type: String,
			required: false,
		},
		currentAbsence: {
			type: Object as () => UserAbsenceResponse | null,
			required: true,
		},
	},
	emits: ['handleSetAbsence', 'reload', 'update:open'],
	data() {
		return {
			request: {
				dateRange: null as string[] | null,
			},
			rules: {
				dateRange: [{ required: true, message: texts.formErrors.reqMissing, trigger: ['blur', 'change'] }],
			},
			waiting: false,
			id: 'EditUserAbsenceForm',
			showErr: false,
			errMsg: texts.user.setAbsenceFailed,
		};
	},
	computed: {
		texts() {
			return texts;
		},
	},
	setup() {
		const formRef = ref<FormInstance | null>(null);

		return {
			formRef,
		};
	},
	methods: {
		checkIfDateInPast,
		handleClose(reload: Boolean) {
			resetForm(this.formRef, this.id);
			this.showErr = false;

			if (reload) {
				this.$emit('reload');
			}

			this.$emit('update:open', false);
		},
		async deleteUserAbsence() {
			if (!this.currentAbsence) return;
			this.waiting = true;

			const deleteAbsenceRequest = await deleteUserAbsence(this.currentAbsence.id);
			if (deleteAbsenceRequest.success) {
				this.handleClose(true);
			} else {
				this.showErr = true;
			}
			this.waiting = false;
		},
		async setUserAbsence() {
			const isValid = await validateForm(this.formRef, this.id);
			if (!isValid || !this.request.dateRange) return;

			this.waiting = true;

			const absenceRequest: AbsenceRequest = {
				userId: this.userId,
				from: getStartOfDayWithTime(this.request.dateRange[0]),
				to: getEndOfDayWithTime(this.request.dateRange[1]),
			};

			const absenceResult = await createUserAbsence(absenceRequest);
			if (absenceResult.success) {
				this.handleClose(true);
			} else {
				this.showErr = true;
			}

			this.waiting = false;
		},
		dialogBeforeClose,
	},
	watch: {
		currentAbsence: {
			immediate: true,
			handler(val: UserAbsenceResponse | null) {
				if (val) {
					this.request.dateRange = [val.from, val.to];
				} else {
					this.request.dateRange = null;
				}
			},
		},
	},
});
</script>
